import { Component, type ErrorInfo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { config } from '@/lib/config';
import { Button } from './Button';

/**
 * App-level error boundary (spec §5.6 / FE-008). A single component that throws during render
 * must never white-screen the whole console — it is caught here and replaced with a recoverable
 * error card. `resetKey` (the current route) clears the boundary on navigation so moving away
 * from the broken view recovers without a full reload.
 *
 * React error boundaries must be class components; the visible fallback is a function component
 * so it can use the i18n hook for a fully localized, RTL-aware message.
 */
interface Props {
  children: ReactNode;
  /** When this value changes, a tripped boundary resets (e.g. pass the route pathname). */
  resetKey?: unknown;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidUpdate(prev: Props) {
    // Navigating to a new route clears a previously-tripped boundary.
    if (this.state.error && prev.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Surface for local debugging; forward to the configured sink in production.
    console.error('Uncaught render error:', error, info.componentStack);
    if (config.errorSinkDsn) {
      // Best-effort, fire-and-forget. The boundary must never throw while reporting.
      try {
        void fetch(config.errorSinkDsn, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({
            message: error.message,
            stack: error.stack,
            componentStack: info.componentStack,
          }),
          keepalive: true,
        }).catch(() => {});
      } catch {
        /* ignore — reporting is never allowed to break the fallback */
      }
    }
  }

  private reset = () => this.setState({ error: null });

  render() {
    if (this.state.error) {
      return <ErrorFallback error={this.state.error} onReset={this.reset} />;
    }
    return this.props.children;
  }
}

/**
 * True when the error is a lazy route chunk that failed to load. After a deploy, an old tab
 * still references the previous release's hashed chunk names, which the server no longer
 * has; "try again" cannot fix that, only a reload onto the new release can.
 */
export function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  return (
    error.name === 'ChunkLoadError' ||
    /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unable to preload CSS/i.test(
      error.message,
    )
  );
}

function ErrorFallback({ error, onReset }: { error: Error; onReset: () => void }) {
  const { t } = useTranslation();
  if (isChunkLoadError(error)) {
    return (
      <div role="alert" className="flex min-h-[60vh] flex-col items-center justify-center gap-md p-2xl text-center">
        <div className="flex max-w-md flex-col items-center gap-sm rounded-md border-[0.5px] border-hairline bg-canvas-paper p-2xl">
          <h2 className="text-title text-ink-deep">{t('ui.updateTitle')}</h2>
          <p className="text-small text-ink-mid">{t('ui.updateBody')}</p>
          <Button className="mt-xs" onClick={() => window.location.reload()}>
            {t('ui.reloadToUpdate')}
          </Button>
        </div>
      </div>
    );
  }
  return (
    <div
      role="alert"
      className="flex min-h-[60vh] flex-col items-center justify-center gap-md p-2xl text-center"
    >
      <div className="flex max-w-md flex-col items-center gap-sm rounded-md border-[0.5px] border-signal-error/40 bg-signal-error-fill p-2xl">
        <p className="eyebrow text-signal-error">{t('ui.error')}</p>
        <h2 className="text-title text-ink-deep">{t('ui.crashTitle')}</h2>
        <p className="text-small text-ink-mid">{t('ui.crashBody')}</p>
        <div className="mt-xs flex items-center gap-sm">
          <Button variant="ghost" onClick={onReset}>
            {t('common.retry')}
          </Button>
          <Button onClick={() => window.location.reload()}>{t('ui.reload')}</Button>
        </div>
        <details className="mt-xs w-full text-start">
          <summary className="cursor-pointer text-label text-ink-mute">{t('ui.reportThis')}</summary>
          <code className="mt-2xs block max-h-[160px] select-all overflow-auto font-mono text-label text-ink-mute">
            {error.message}
          </code>
        </details>
      </div>
    </div>
  );
}
